import React from 'react';
import { ParagraphText, StackLayout } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { useUserInfo } from 'hooks/use-use-info.hook';
import {
  sendProfileSettingsButtonClickedEvent,
  sendAccountSetupButtonClickedEvent,
} from 'utils/analytics-events';
import { EditableDetailItem } from './editable-detail-item.component';
import { DetailSection } from './detail-section.component';

interface PersonalInfoProps {
  firstName?: string;
  lastName?: string;
  preferredFirstName: string;
  alwaysEdit?: boolean;
  showBorder?: boolean;
  hideButtons?: boolean;
  description?: string;
  noPadding?: boolean;
  hasSubtitle?: boolean;
  disableUnsavedModal?: boolean;
  onlyPreferredName?: boolean;
  isAccountSetup?: boolean;
  onInputChange?: (id: string, value: string) => void;
  onSave?: (values: Record<string, string>) => void;
}

export const PersonalInfoComponent: React.FC<PersonalInfoProps> = ({
  firstName,
  lastName,
  preferredFirstName,
  alwaysEdit = false,
  showBorder = true,
  hideButtons = false,
  noPadding = false,
  description,
  disableUnsavedModal = false,
  onlyPreferredName = false,
  hasSubtitle = false,
  isAccountSetup = false,
  onInputChange,
  onSave,
}) => {
  const { contents, handleSave } = useUserInfo({
    firstName,
    lastName,
    description,
    preferredFirstName,
    onlyPreferredName,
    onInputChange,
    onSave,
  });
  const { formatMessage } = useIntl();

  const handleSaveWithAnalytics = async (values: Record<string, string>) => {
    sendProfileSettingsButtonClickedEvent(
      'save',
      undefined,
      'profile settings - personal information',
    );
    await handleSave(values);
  };

  return (
    <DetailSection
      title="STR_PERSONAL_INFORMATION"
      contents={contents}
      onSave={handleSaveWithAnalytics}
      alwaysEdit={alwaysEdit}
      showBorder={showBorder}
      hideButtons={hideButtons}
      noPadding={noPadding}
      disableUnsavedModal={disableUnsavedModal}
      renderEditContent={({
        contents: editContents,
        editValues,
        onChange,
        isMobile,
      }) => (
        <>
          {hasSubtitle && (
            <ParagraphText
              css={{
                fontSize: '$bodyTwo',
                paddingBottom: '$one',
              }}
            >
              {formatMessage({ id: 'STR_PROFILE_SETTINGS_PERSONAL_INFO_DESC' })}
            </ParagraphText>
          )}
          <StackLayout
            orientation="horizontal"
            css={{
              display: 'grid',
              gridTemplateColumns: isMobile
                ? '1fr'
                : 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: isMobile ? '$two' : '$one',
              width: '100%',
            }}
          >
            {editContents.map((content) => (
              <EditableDetailItem
                key={content.labelId}
                id={content.labelId}
                labelId={content.labelId}
                description={description}
                value={editValues[content.labelId] ?? content.value}
                type={content.type!}
                isDisabled={content.isDisabled}
                options={content.options}
                onChange={(id, value) => {
                  if (
                    id === 'STR_PREFERRED_FIRST_NAME_OPTIONAL' ||
                    id === 'STR_PREFERRED_FIRST_NAME'
                  ) {
                    if (isAccountSetup) {
                      sendAccountSetupButtonClickedEvent('edit name');
                    } else {
                      sendProfileSettingsButtonClickedEvent(
                        'edit preferred name',
                        undefined,
                        'profile settings - personal information',
                      );
                    }
                  }
                  onChange(id, value);
                  onInputChange?.(id, value);
                }}
              />
            ))}
          </StackLayout>
        </>
      )}
    />
  );
};
